import z from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { OlapScoresResult, OlapScoresResultSchema } from "../olapScores/olapScoresSchema";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import type { CompanyOption } from "src/components/Selector/CompanySelector/CompanyAutocomplete";
import { getRemoteEntityState, type RemoteEntityState } from "src/api/remoteEntityState";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

export const getOnePackagingKey = "getOnePackaging" as const;

type Options = {
    packagingId: (string | number) | undefined;
    scoresQuery?: OlapScoresQuery;
};
export const useGetOnePackaging = (options: Options) => {
    const { packagingId, scoresQuery = {} } = options;
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const campaignId = currentCampaign?.id;
    const organizationId = selectedCompany?.id;
    const isEnabled = packagingId !== undefined && organizationId !== undefined;

    const query = useQuery({
        queryKey: [getOnePackagingKey, organizationId ?? null, packagingId, scoresQuery, campaignId],
        cacheTime: 0,
        enabled: isEnabled,
        queryFn: async () => {
            if (packagingId === undefined || organizationId === undefined) {
                throw new Error("Cannot load a packaging without an id and selected organization");
            }
            const url = `/api/packagings/${packagingId}`;

            const response = await loggedInApiCall.get<Packaging>(url, {
                params: { ...scoresQuery, organization_id: organizationId, campaign: campaignId },
            });
            return response;
        },
        select: (request) => {
            const data = zodParseWithDetailedLog(request.data, PackagingZodSchema, {
                operation: "GET",
                endpoint: "/packagings/:id",
            });
            return normalizePackagingType(data);
        },
    });
    const packaging = isEnabled ? query.data : undefined;

    const packagingLoadState = getRemoteEntityState({
        entity: packaging,
        entityId: isEnabled ? packagingId : undefined,
        error: query.error,
        isLoading: query.isLoading,
    });

    return {
        ...query,
        data: packaging,
        packaging,
        packagingLoadState,
    };
};

/**
 * Sub-schema for the embedded `composition[].child` reference. The API
 * sends a fully-serialised `PackagingElement` (or nested composite) but the
 * UI only ever needs `id` + `name` to repaint the picker — anything else is
 * tolerated via `passthrough()` so we never lose data on round-trips.
 */
export const PackagingChildRefSchema = z
    .object({
        "@id": z.string(),
        id: z.number(),
        name: z.string(),
    })
    .passthrough();
export type PackagingChildRef = z.infer<typeof PackagingChildRefSchema>;

/**
 * Sub-schema for one row of `PackagingComposite.composition`. Mirrors the
 * sure-side `PackagingComposition` entity (`child`, `amount`, `position`).
 * Only present on composite payloads.
 */
export const PackagingCompositionSchema = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    child: PackagingChildRefSchema,
    amount: z.number(),
    position: z.number(),
    isMain: z.boolean().optional(),
});
export type PackagingComposition = z.infer<typeof PackagingCompositionSchema>;

export type Packaging = {
    "@id": IRI;
    /**
     * JSON-LD discriminator surfaced by API Platform: `"PackagingElement"`
     * for elementary packagings, `"PackagingComposite"` for composites.
     * Drives the form-dispatch logic in {@code UpdatePackaging}. Optional
     * because legacy clients / fixtures may serialise without `@type`.
     */
    "@type"?: string;
    type?: "element" | "composite";
    id: number;
    name: string;
    externalId?: string;
    /**
     * Optional on the wire: composite packagings (any mode) don't ship `weight`
     * at all — their effective weight is derived server-side from the sum of
     * their children's weight × amount. Only `PackagingElement` rows ever
     * carry a numeric `weight`. Consumers must `?? 0` (or branch on the
     * `@type` discriminator) before doing arithmetic.
     */
    weight?: number | null;
    ssqId?: number;
    /**
     * `PackagingElement` only: id of the sure `PackagingReference` (material).
     * Absent for composites.
     */
    packagingReferenceId?: number;
    /** `PackagingElement` only: whether the material is recycled. */
    recycled?: boolean;
    /**
     * `PackagingElement` only: 0–100 percentage when `recycled === true`.
     * NULL when not provided.
     */
    recycledProportion?: number | null;
    /** `PackagingElement` only: whether the material is recyclable. */
    recyclable?: boolean | null;
    /** `PackagingElement` only: reusable flag (e.g. returnable bottle). */
    reuse?: boolean;
    shares?: PackagingShare[];
    /**
     * Mode A (frozen composite) only: id of the sure `PackagingReferenceComposite`
     * the user picked. NULL/absent for mode B (user-built composite) and for
     * elements.
     */
    packagingReferenceCompositeId?: number | null;
    /**
     * Mode B (user-built composite) only: ordered list of child packagings
     * assembled by the user. Empty/absent for mode A composites and for
     * elements.
     */
    composition?: PackagingComposition[];
    olapScores: OlapScoresResult;
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    tags?: Record<string, string[]>; // Format objet groupé par type de tag
};

export type PackagingShare = {
    "@id": string;
    id: number;
    organization: CompanyOption;
};

export type PackagingLoadState = RemoteEntityState<Packaging>;

export const PackagingZodSchema: z.ZodSchema<Packaging> = z.object({
    "@id": z.string(),
    "@type": z.string().optional(),
    type: z.enum(["element", "composite"]).optional(),
    id: z.number(),
    name: z.string(),
    externalId: z.string().optional(),
    weight: z.number().nullable().optional(),
    ssqId: z.number().optional(),
    packagingReferenceId: z.number().optional(),
    recycled: z.boolean().optional(),
    recycledProportion: z.number().nullable().optional(),
    recyclable: z.boolean().nullable().optional(),
    reuse: z.boolean().optional(),
    shares: z
        .array(
            z.object({
                "@id": z.string(),
                id: z.number(),
                organization: z.object({
                    "@id": z.string(),
                    id: z.number(),
                    name: z.string(),
                }),
            }),
        )
        .optional(),
    packagingReferenceCompositeId: z.number().nullable().optional(),
    composition: z.array(PackagingCompositionSchema).optional(),
    olapScores: OlapScoresResultSchema,
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
    tags: z.any(),
});

const normalizePackagingType = (packaging: Packaging): Packaging => {
    if (packaging["@type"] !== undefined) {
        return packaging;
    }

    return {
        ...packaging,
        "@type": packaging.type === "composite" ? "PackagingComposite" : "PackagingElement",
    };
};
